import { useCallback, useEffect, useState, useMemo } from 'react';
import {
  Stack,
  Box,
  Typography,
  Toolbar,
  Divider,
  Button,
  Link,
} from '@mui/material';

import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import type {
  GridColDef,
  GridPaginationModel,
  GridRowSelectionModel,
  GridRenderCellParams,
  GridRowId,
} from '@mui/x-data-grid';
import { Link as RouterLink, useNavigate } from 'react-router';

import { deleteChat, getChats } from '@/actions/chat';
import { useUser } from '@/providers/UserContext';
import LoadingMask from '@/components/shared/LoadingMask';
import CHAT_TYPES from '@/components/chatTypes';
import ChatDeleteDialog from '@/components/shared/ChatDeleteDialog';
import { useApi } from '@/providers/ApiContext';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import type { Chat } from '@/types/apiTypes';
import type { EntityId } from '@/types/apiTypes';

const NEW_CHAT_NAME = 'New Audit';

function ExternalAudits(): React.JSX.Element {
  const user = useUser();
  const api = useApi();
  const navigate = useNavigate();
  const [chats, setChats] = useState<Chat[] | null>(null);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set<GridRowId>(),
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(50);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [cursors, setCursors] = useState<Record<number, string>>({});

  const fetchChats = useCallback(
    async (page: number = 0, startKey: string | null = null) => {
      if (!api) return;
      setLoading(true);
      try {
        const response = (await getChats(
          api,
          CHAT_TYPES.QUARTERMASTER,
          pageSize,
          startKey || undefined,
        )) as unknown as { results: Chat[]; last_key: string | null };
        setChats((prevChats) =>
          startKey === null
            ? response.results
            : [...(prevChats || []), ...response.results],
        );
        setHasNextPage(response.last_key !== null);
        if (response.last_key) {
          setCursors((prev) => ({
            ...prev,
            [page + 1]: response.last_key!,
          }));
        }
      } catch (error) {
        console.log(error);
        setChats([]);
      } finally {
        setLoading(false);
      }
    },
    [api, pageSize],
  );

  useEffect(() => {
    if (user?.entity_id && !chats) {
      setCursors({});
      fetchChats();
    }
  }, [user?.entity_id, chats, fetchChats]);

  const handleDeleteChat = async (chat_id: EntityId): Promise<void> => {
    if (!api) return;
    await deleteChat(api, chat_id);
    const new_chats = [...(chats || [])].filter(
      (chat) => chat.entity_id !== chat_id,
    );
    setChats(new_chats);
  };

  const handleRowSelection = useCallback((value: GridRowSelectionModel) => {
    setRowSelectionModel(value);
  }, []);

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      const newPage = model.page;
      const newPageSize = model.pageSize;

      if (newPageSize !== pageSize) {
        setPageSize(newPageSize);
        setCurrentPage(0);
        setCursors({});
        fetchChats(0);
        return;
      }

      if (newPage > currentPage && hasNextPage) {
        const nextCursor = cursors[newPage];
        if (nextCursor) {
          fetchChats(newPage, nextCursor);
        }
      }
      setCurrentPage(newPage);
    },
    [currentPage, cursors, fetchChats, hasNextPage, pageSize],
  );

  const renderName = useCallback((params: GridRenderCellParams<Chat>) => {
    return (
      <Link
        component={RouterLink}
        to={`/quartermaster/externalAudit/${params.id}`}
      >
        {params.row.name}
      </Link>
    );
  }, []);

  const renderStatus = useCallback((params: GridRenderCellParams<Chat>) => {
    if (params.row.name === NEW_CHAT_NAME) {
      return 'In Progress';
    }
    return 'Complete';
  }, []);

  const convertDate = useCallback((value: string | null | undefined) => {
    return value ? new Date(value.replace(' ', 'T') + 'Z') : null;
  }, []);

  const columns = useMemo<GridColDef<Chat>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        type: 'string',
        renderCell: renderName,
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        flex: 0.5,
        renderCell: renderStatus,
      },
      {
        field: 'created_at',
        headerName: 'Started',
        type: 'dateTime',
        flex: 0.75,
        valueGetter: (_value: unknown, row: Chat) =>
          convertDate(row.created_at),
      },
      {
        field: 'updated_at',
        headerName: 'Last Updated',
        type: 'dateTime',
        flex: 0.75,
        valueGetter: (_value: unknown, row: Chat) =>
          convertDate(row.updated_at),
      },
    ],
    [renderName, convertDate, renderStatus],
  );

  if (!chats) {
    return <LoadingMask />;
  }

  return (
    <>
      <Toolbar
        sx={{
          minHeight: '55px !important',
        }}
      >
        <Typography variant='h6' sx={{ mr: 4 }}>
          Audits
        </Typography>
        <Stack direction={'row'} spacing={2}>
          <Button
            onClick={() => {
              navigate('/quartermaster/externalAudit/edit');
            }}
            variant='contained'
            startIcon={<AddCircleOutlineOutlinedIcon />}
          >
            New Audit
          </Button>
          <Button
            onClick={() => {
              const selectedIds = Array.from(rowSelectionModel.ids);
              navigate({
                pathname: '/quartermaster/externalAudit/edit',
                search: `?audit_id=${selectedIds[0]}`,
              });
            }}
            disabled={rowSelectionModel.ids.size !== 1}
            startIcon={<EditOutlinedIcon />}
          >
            Edit
          </Button>
          <Button
            onClick={() => setDeleteOpen(true)}
            disabled={rowSelectionModel.ids.size !== 1}
            startIcon={<DeleteOutlinedIcon />}
          >
            Delete
          </Button>
        </Stack>
      </Toolbar>
      <Divider />
      <Box
        sx={{
          height: 'calc(100vh - 200px)',
          width: '100%',
        }}
      >
        <DataGrid
              checkboxSelection
              columnHeaderHeight={35}
              sx={(theme) => ({
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.grey[100],
                },
              })}
              getRowId={(row) => row.entity_id}
              columns={columns}
              rows={chats || []}
              onRowSelectionModelChange={handleRowSelection}
              rowSelectionModel={rowSelectionModel}
              showToolbar
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  sx: {
                    button: {
                      color: 'black',
                    },
                    svg: {
                      color: 'black',
                    },
                  },
                  showQuickFilter: true,
                },
              }}
              paginationMode='server'
              rowCount={hasNextPage ? -1 : chats.length}
              pageSizeOptions={[50, 100]}
              paginationModel={{
                page: currentPage,
                pageSize: pageSize,
              }}
              onPaginationModelChange={handlePaginationModelChange}
              loading={loading}
            />
      </Box>
      <ChatDeleteDialog
        key={`delete_${chats?.find((chat) => {
          const selectedIds = Array.from(rowSelectionModel.ids);
          return chat.entity_id === selectedIds[0];
        })?.updated_at || ''}`}
        chat={{ entity_id: Array.from(rowSelectionModel.ids)[0] } as Chat}
        open={deleteOpen}
        deleteChat={handleDeleteChat}
        handleClose={() => setDeleteOpen(false)}
      />
    </>
  );
}

export default ExternalAudits;
